import { useLayoutEffect, useState } from 'react';

import type { ScreenShareTile } from './roomState';
import { trackArrivals } from './shareStage';

/**
 * The stage-pick rule shared with the webinar stage: an explicit pick (a rail tile's pin)
 * stays the stage until its share stops (a stale pick is cleared as soon as the share
 * disappears, so re-sharing starts from the recency default).
 */
export function useShareStagePick(shares: readonly ScreenShareTile[]): {
  readonly picked: string | null;
  readonly pickIdentity: (identity: string) => void;
} {
  const [picked, setPicked] = useState<string | null>(null);

  useLayoutEffect(() => {
    setPicked((current) =>
      current !== null && shares.some((share) => share.identity === current) ? current : null,
    );
  }, [shares]);

  return {
    picked,
    pickIdentity: (identity: string): void => {
      setPicked(identity);
    },
  };
}

/** Arrival order of the live shares, most recent first (append-only while mounted). */
export function useArrivals(shares: readonly ScreenShareTile[]): readonly string[] {
  const identities = shares.map((share) => share.identity);
  const [arrivals, setArrivals] = useState<readonly string[]>([]);

  useLayoutEffect(() => {
    setArrivals((current) => trackArrivals(current, identities));
    // identities is a fresh array every render, so this runs after each parent render;
    // trackArrivals is idempotent, the extra runs are cheap no-ops.
  }, [identities]);

  return arrivals;
}
